import { createContext, useCallback, useContext, useState, type ReactNode } from 'react'
import { Modal } from '../components/ui/Modal'

interface ConfirmOptions {
  title: string
  message: string
  okLabel?: string
  danger?: boolean
}

interface CustomOptions {
  title: string
  render: (close: () => void) => ReactNode
  wide?: boolean
}

interface ModalContextValue {
  confirm: (options: ConfirmOptions) => Promise<boolean>
  alert: (title: string, message: string) => Promise<void>
  openCustom: (options: CustomOptions) => void
  closeCustom: () => void
}

const ModalContext = createContext<ModalContextValue | undefined>(undefined)

type ModalState =
  | { kind: 'none' }
  | { kind: 'confirm'; options: ConfirmOptions; resolve: (value: boolean) => void }
  | { kind: 'alert'; title: string; message: string; resolve: () => void }
  | { kind: 'custom'; options: CustomOptions }

export function ModalProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<ModalState>({ kind: 'none' })

  const confirm = useCallback((options: ConfirmOptions) => {
    return new Promise<boolean>((resolve) => {
      setState({ kind: 'confirm', options, resolve })
    })
  }, [])

  const alert = useCallback((title: string, message: string) => {
    return new Promise<void>((resolve) => {
      setState({ kind: 'alert', title, message, resolve })
    })
  }, [])

  const openCustom = useCallback((options: CustomOptions) => {
    setState({ kind: 'custom', options })
  }, [])

  const closeCustom = useCallback(() => setState({ kind: 'none' }), [])

  const close = () => {
    setState((current) => {
      if (current.kind === 'confirm') current.resolve(false)
      if (current.kind === 'alert') current.resolve()
      return { kind: 'none' }
    })
  }

  return (
    <ModalContext.Provider value={{ confirm, alert, openCustom, closeCustom }}>
      {children}
      {state.kind === 'confirm' && (
        <Modal title={state.options.title}>
          <p className="text-text mb-4">{state.options.message}</p>
          <div className="flex justify-end gap-2">
            <button className="rounded-lg border border-line bg-panel-2 px-3 py-2 text-sm text-text" onClick={close}>
              Cancelar
            </button>
            <button
              className={
                state.options.danger
                  ? 'rounded-lg bg-danger px-3 py-2 text-sm font-bold text-white'
                  : 'rounded-lg bg-accent px-3 py-2 text-sm font-bold text-[#06101d]'
              }
              onClick={() => {
                state.resolve(true)
                setState({ kind: 'none' })
              }}
            >
              {state.options.okLabel ?? 'Aceptar'}
            </button>
          </div>
        </Modal>
      )}
      {state.kind === 'alert' && (
        <Modal title={state.title}>
          <p className="text-text mb-4">{state.message}</p>
          <div className="flex justify-end">
            <button className="rounded-lg bg-accent px-3 py-2 text-sm font-bold text-[#06101d]" onClick={close}>
              Entendido
            </button>
          </div>
        </Modal>
      )}
      {state.kind === 'custom' && (
        <Modal title={state.options.title} wide={state.options.wide}>
          {state.options.render(closeCustom)}
        </Modal>
      )}
    </ModalContext.Provider>
  )
}

export function useModal(): ModalContextValue {
  const ctx = useContext(ModalContext)
  if (!ctx) throw new Error('useModal debe usarse dentro de <ModalProvider>')
  return ctx
}
